import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LogoBadge from "@/components/LogoBadge";
export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/verify", null, {
        headers: { "X-Admin-Password": password },
      });
      setAuthed(true);
      localStorage.setItem("sfa_admin_pw", password);
      toast.success("Welcome back.");
      await loadData(password);
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (pw) => {
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/bookings", { headers: { "X-Admin-Password": pw } }),
        api.get("/admin/subscriptions", { headers: { "X-Admin-Password": pw } }),
      ]);
      setBookings(b.data);
      setSubs(s.data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    const pw = localStorage.getItem("sfa_admin_pw");
    if (pw) {
      setPassword(pw);
      api
        .post("/admin/verify", null, { headers: { "X-Admin-Password": pw } })
        .then(() => {
          setAuthed(true);
          loadData(pw);
        })
        .catch(() => localStorage.removeItem("sfa_admin_pw"));
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("sfa_admin_pw");
    setAuthed(false);
    setPassword("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F1EDDE] p-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#FFFFFF] border border-[#C9BEA9] p-10"
        >
          <div className="flex flex-col items-center gap-2 mb-8">
            <LogoBadge variant="stone" size="md" />
            <div className="text-[0.6rem] uppercase tracking-[0.3em] text-[#8C8580] mt-2">
              Admin Access
            </div>
          </div>
          <input
            required
            type="password"
            data-testid="admin-password-input"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="underline-input"
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="btn-primary w-full mt-8 disabled:opacity-60"
          >
            {loading ? "Verifying..." : "Enter"}
          </button>
          <a
            href="/"
            className="mt-5 block text-center text-[0.65rem] uppercase tracking-[0.25em] text-[#8C8580] hover:text-[#161618]"
          >
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1EDDE] text-[#161618]">
      <header className="border-b border-[#C9BEA9] bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LogoBadge variant="stone" size="xs" />
            <div className="border-l border-[#C9BEA9] pl-4">
              <div className="font-serif text-xl text-[#161618]">Admin Dashboard</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="text-[0.7rem] uppercase tracking-[0.22em] hover:text-[#B6AA96]"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex gap-8 border-b border-[#C9BEA9] mb-10">
          {["bookings", "subs"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`pb-4 text-[0.7rem] uppercase tracking-[0.25em] border-b-2 transition-all ${
                tab === t
                  ? "border-[#161618] text-[#161618]"
                  : "border-transparent text-[#8C8580] hover:text-[#161618]"
              }`}
            >
              {t === "bookings" ? `Bookings (${bookings.length})` : `Subscriptions (${subs.length})`}
            </button>
          ))}
        </div>

        {tab === "bookings" ? (
          <Table
            data={bookings}
            testId="bookings-table"
            columns={[
              { key: "created_at", label: "Date", format: (v) => new Date(v).toLocaleString() },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
              { key: "treatment", label: "Treatment" },
              { key: "preferred_date", label: "Preferred" },
              { key: "message", label: "Message" },
            ]}
            empty="No bookings yet."
          />
        ) : (
          <Table
            data={subs}
            testId="subs-table"
            columns={[
              { key: "created_at", label: "Date", format: (v) => new Date(v).toLocaleString() },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
              { key: "tier", label: "Tier", format: (v) => v?.toUpperCase() },
              { key: "status", label: "Status" },
              { key: "notes", label: "Notes" },
            ]}
            empty="No subscriptions yet."
          />
        )}
      </div>
    </div>
  );
}

const Table = ({ data, columns, empty, testId }) => {
  if (!data.length) {
    return (
      <div
        data-testid={`${testId}-empty`}
        className="bg-[#FFFFFF] border border-[#C9BEA9] p-12 text-center text-[#8C8580] font-serif italic text-xl"
      >
        {empty}
      </div>
    );
  }
  return (
    <div data-testid={testId} className="overflow-x-auto border border-[#C9BEA9] bg-[#FFFFFF]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#C9BEA9] bg-[#E6DFCC]">
            {columns.map((c) => (
              <th
                key={c.key}
                className="text-left px-5 py-4 text-[0.65rem] uppercase tracking-[0.2em] text-[#4D4A47] font-medium"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id || i}
              className="border-b border-[#E6DFCC] hover:bg-[#F1EDDE] transition-colors"
            >
              {columns.map((c) => (
                <td key={c.key} className="px-5 py-4 align-top text-[#161618] font-light">
                  {c.format ? c.format(row[c.key]) : row[c.key] || "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
